<script setup lang="ts">
import { ref, computed, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import {
  useGetFieldParts,
  useGetFieldObservations,
  useCreateFieldPart,
  useUpdateFieldPart,
  useDeleteFieldPart,
  useDuplicateFieldPart,
} from "@/stores/queries/field-observations.query";
import {
  useGetDiagnostic,
  useApplyPartsSuggestions,
} from "@/stores/queries/diagnostics.query";
import type { FieldPart } from "@/types/field-observations.types";
import FieldHeader from "./FieldHeader.vue";
import MobileConfirmModal from "./MobileConfirmModal.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import AIPartsSuggestionsBanner from "./AIPartsSuggestionsBanner.vue";
import SaveAsTemplateModal from "./SaveAsTemplateModal.vue";
import InsertFromTemplateModal from "./InsertFromTemplateModal.vue";

const props = defineProps<{
  diagnosticId: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();
const diagnosticId = toRef(props, "diagnosticId");

// Queries
const { data: parts } = useGetFieldParts(diagnosticId);
const { data: observations } = useGetFieldObservations(diagnosticId);
const { data: diagnostic } = useGetDiagnostic(diagnosticId);
const { mutateAsync: createPart, isPending: isCreating } = useCreateFieldPart();
const { mutateAsync: updatePart } = useUpdateFieldPart();
const { mutateAsync: deletePart, isPending: isDeleting } = useDeleteFieldPart();
const { mutateAsync: duplicatePart, isPending: isDuplicating } =
  useDuplicateFieldPart();
const { mutateAsync: applyPartsSuggestions, isPending: isApplyingSuggestions } =
  useApplyPartsSuggestions();

// AI Suggestions - computed for the banner
const hasLinkedObservations = computed(() => {
  if (!observations.value) return false;
  return observations.value.some(
    (obs) => obs.partId !== null && obs.partId !== undefined,
  );
});

const handleApplyPartsSuggestions = async () => {
  await applyPartsSuggestions(props.diagnosticId);
};

// Get children of a part
const getChildParts = (parentId: number): FieldPart[] => {
  if (!parts.value) return [];
  return parts.value.filter((p) => p.parentId === parentId);
};

// Check if a part has observations
const getObservationCount = (partId: number): number => {
  if (!observations.value) return 0;
  return observations.value.filter((o) => o.partId === partId).length;
};

// Check if a part has children
const hasChildren = (partId: number): boolean => {
  return getChildParts(partId).length > 0;
};

// Expanded parts
const expandedParts = ref<Set<number>>(new Set());

const toggleExpand = (partId: number) => {
  if (expandedParts.value.has(partId)) {
    expandedParts.value.delete(partId);
  } else {
    expandedParts.value.add(partId);
  }
  expandedParts.value = new Set(expandedParts.value);
};

// Flatten tree for rendering
interface FlatPart {
  part: FieldPart;
  level: number;
}

const flattenParts = (
  partsList: FieldPart[],
  parentId: number | null,
  level: number,
): FlatPart[] => {
  const result: FlatPart[] = [];
  const children = partsList.filter((p) =>
    parentId === null ? !p.parentId : p.parentId === parentId,
  );

  for (const part of children) {
    result.push({ part, level });
    if (expandedParts.value.has(part.id)) {
      result.push(...flattenParts(partsList, part.id, level + 1));
    }
  }

  return result;
};

const flatParts = computed(() => {
  if (!parts.value) return [];
  return flattenParts(parts.value, null, 0);
});

// Edit mode
const showEditModal = ref(false);
const editingPart = ref<FieldPart | null>(null);
const editingName = ref("");
const isSavingEdit = ref(false);

const startEdit = (part: FieldPart) => {
  editingPart.value = part;
  editingName.value = part.name;
  showEditModal.value = true;
};

const cancelEdit = () => {
  showEditModal.value = false;
  editingPart.value = null;
  editingName.value = "";
};

const saveEdit = async () => {
  if (!editingPart.value || !editingName.value.trim()) return;

  isSavingEdit.value = true;
  try {
    await updatePart({
      id: editingPart.value.id,
      payload: { name: editingName.value.trim() },
      diagnosticId: props.diagnosticId,
    });
    cancelEdit();
  } finally {
    isSavingEdit.value = false;
  }
};

// Add part
const showAddModal = ref(false);
const addPartParentId = ref<number | undefined>();
const newPartName = ref("");

const openAddModal = (parentId?: number) => {
  addPartParentId.value = parentId;
  newPartName.value = "";
  showAddModal.value = true;
};

const handleAddPart = async () => {
  if (!newPartName.value.trim()) return;

  await createPart({
    name: newPartName.value.trim(),
    diagnosticId: props.diagnosticId,
    parentId: addPartParentId.value,
  });

  // Auto-expand parent if adding sub-part
  if (addPartParentId.value) {
    expandedParts.value.add(addPartParentId.value);
    expandedParts.value = new Set(expandedParts.value);
  }

  showAddModal.value = false;
  newPartName.value = "";
  addPartParentId.value = undefined;
};

// Delete part
const showDeleteConfirm = ref(false);
const showCannotDelete = ref(false);
const cannotDeleteReason = ref("");
const partToDelete = ref<FieldPart | null>(null);

const confirmDelete = (part: FieldPart) => {
  partToDelete.value = part;

  // Check if has children
  if (hasChildren(part.id)) {
    cannotDeleteReason.value = t(
      "dashboard:fieldObservations.partsTree.cannotDeleteHasChildren",
    );
    showCannotDelete.value = true;
    return;
  }

  // Check if has observations
  const obsCount = getObservationCount(part.id);
  if (obsCount > 0) {
    cannotDeleteReason.value = t(
      "dashboard:fieldObservations.partsTree.cannotDeleteHasObservations",
      { count: obsCount },
    );
    showCannotDelete.value = true;
    return;
  }

  showDeleteConfirm.value = true;
};

const handleDelete = async () => {
  if (!partToDelete.value) return;

  await deletePart({
    id: partToDelete.value.id,
    diagnosticId: props.diagnosticId,
  });

  showDeleteConfirm.value = false;
  partToDelete.value = null;
};

// Template modals
const showSaveTemplateModal = ref(false);
const partToSaveAsTemplate = ref<FieldPart | null>(null);
const showInsertTemplateModal = ref(false);
const insertTemplateParentId = ref<number | undefined>();

const openSaveAsTemplate = (part: FieldPart) => {
  partToSaveAsTemplate.value = part;
  showSaveTemplateModal.value = true;
};

const openSaveTreeAsTemplate = () => {
  partToSaveAsTemplate.value = null;
  showSaveTemplateModal.value = true;
};

const openInsertTemplate = (parentId?: number) => {
  insertTemplateParentId.value = parentId;
  showInsertTemplateModal.value = true;
};

const handleTemplateInserted = () => {
  // Auto-expand parent if inserting as sub-part
  if (insertTemplateParentId.value) {
    expandedParts.value.add(insertTemplateParentId.value);
    expandedParts.value = new Set(expandedParts.value);
  }
};

// Duplicate part (single copy with same name)
const handleDuplicate = async (part: FieldPart) => {
  await duplicatePart({
    id: part.id,
    names: [part.name],
    diagnosticId: props.diagnosticId,
  });
};

// Multiply modal
const showMultiplyModal = ref(false);
const multiplyPart = ref<FieldPart | null>(null);
const multiplyNames = ref<string[]>([]);

const generateDefaultName = (baseName: string, index: number): string => {
  const stripped = baseName.replace(/\s*\d+\s*$/, "").trim();
  return `${stripped} ${index}`;
};

const openMultiplyModal = (part: FieldPart) => {
  multiplyPart.value = part;
  multiplyNames.value = [1, 2, 3].map((i) => generateDefaultName(part.name, i));
  showMultiplyModal.value = true;
};

const addMultiplyCopy = () => {
  const nextIndex = multiplyNames.value.length + 1;
  multiplyNames.value.push(
    generateDefaultName(multiplyPart.value?.name ?? "", nextIndex),
  );
};

const removeMultiplyCopy = () => {
  if (multiplyNames.value.length > 2) {
    multiplyNames.value.pop();
  }
};

const handleMultiply = async () => {
  if (!multiplyPart.value) return;

  await duplicatePart({
    id: multiplyPart.value.id,
    names: multiplyNames.value.filter((n) => n.trim()),
    diagnosticId: props.diagnosticId,
  });

  // Delete the original part (it's replaced by the copies)
  await deletePart({
    id: multiplyPart.value.id,
    diagnosticId: props.diagnosticId,
  });

  showMultiplyModal.value = false;
  multiplyPart.value = null;
};
</script>

<template>
  <div class="parts-tree-editor">
    <FieldHeader
      :title="t('dashboard:fieldObservations.partsTree.title')"
      @back="emit('close')"
    />

    <!-- AI Suggestions Banner -->
    <AIPartsSuggestionsBanner
      :is-extracting="diagnostic?.isExtractingData ?? false"
      :parts-tree="diagnostic?.partsTree"
      :has-linked-observations="hasLinkedObservations"
      :is-applying="isApplyingSuggestions"
      @apply="handleApplyPartsSuggestions"
    />

    <div class="editor-content">
      <div class="editor-content-inner">
        <!-- Empty state -->
        <div v-if="!parts || parts.length === 0" class="empty-state">
          <PhosphorIcon name="tree-structure" :size="48" class="empty-icon" />
          <p>{{ t("dashboard:fieldObservations.partsTree.empty") }}</p>
        </div>

        <!-- Parts tree -->
        <div v-else class="parts-tree">
          <div
            v-for="{ part, level } in flatParts"
            :key="part.id"
            class="part-item"
            :style="{ paddingLeft: level * 24 + 16 + 'px' }"
          >
            <!-- Left border for hierarchy -->
            <div
              v-if="level > 0"
              class="level-indicator"
              :style="{ left: (level - 1) * 24 + 20 + 'px' }"
            />

            <button
              v-if="hasChildren(part.id)"
              class="btn-expand"
              @click="toggleExpand(part.id)"
            >
              <PhosphorIcon
                name="caret-right"
                :size="16"
                class="expand-icon"
                :class="{ expanded: expandedParts.has(part.id) }"
              />
            </button>
            <span v-else class="expand-spacer" />

            <span class="part-name">{{ part.name }}</span>

            <span
              v-if="getObservationCount(part.id) > 0"
              class="observation-badge"
            >
              {{ getObservationCount(part.id) }}
            </span>

            <div class="part-actions">
              <el-dropdown
                trigger="click"
                @command="
                  (cmd: string) => {
                    if (cmd === 'add-sub') openAddModal(part.id);
                    else if (cmd === 'edit') startEdit(part);
                    else if (cmd === 'duplicate') handleDuplicate(part);
                    else if (cmd === 'multiply') openMultiplyModal(part);
                    else if (cmd === 'save-template') openSaveAsTemplate(part);
                    else if (cmd === 'insert-template')
                      openInsertTemplate(part.id);
                    else if (cmd === 'delete') confirmDelete(part);
                  }
                "
              >
                <button class="btn-action btn-primary">
                  <PhosphorIcon name="dots-three" :size="18" />
                </button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="add-sub">
                      <PhosphorIcon name="plus" :size="16" class="me-2" />
                      {{ t("dashboard:fieldObservations.part.addSubPart") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="edit">
                      <PhosphorIcon
                        name="pencil-simple"
                        :size="16"
                        class="me-2"
                      />
                      {{ t("common:edit") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="duplicate">
                      <PhosphorIcon name="copy" :size="16" class="me-2" />
                      {{ t("dashboard:fieldObservations.partsTree.duplicate") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="multiply">
                      <PhosphorIcon name="stack" :size="16" class="me-2" />
                      {{ t("dashboard:fieldObservations.partsTree.multiply") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="save-template">
                      <PhosphorIcon
                        name="floppy-disk"
                        :size="16"
                        class="me-2"
                      />
                      {{
                        t(
                          "dashboard:fieldObservations.partsTree.saveAsTemplate",
                        )
                      }}
                    </el-dropdown-item>
                    <el-dropdown-item command="insert-template">
                      <PhosphorIcon
                        name="folder-open"
                        :size="16"
                        class="me-2"
                      />
                      {{
                        t(
                          "dashboard:fieldObservations.partsTree.insertFromTemplate",
                        )
                      }}
                    </el-dropdown-item>
                    <el-dropdown-item
                      command="delete"
                      divided
                      class="text-danger"
                    >
                      <PhosphorIcon name="trash" :size="16" class="me-2" />
                      {{ t("common:delete") }}
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>

        <!-- Root action buttons -->
        <div class="root-actions">
          <button class="btn-add-root" @click="openAddModal()">
            <PhosphorIcon name="plus" :size="20" />
            <span>{{ t("dashboard:fieldObservations.part.add") }}</span>
          </button>
          <button class="btn-insert-template" @click="openInsertTemplate()">
            <PhosphorIcon name="folder-open" :size="20" />
            <span>{{
              t("dashboard:fieldObservations.partsTree.insertFromTemplate")
            }}</span>
          </button>
          <button
            v-if="parts && parts.length > 0"
            class="btn-insert-template"
            @click="openSaveTreeAsTemplate"
          >
            <PhosphorIcon name="floppy-disk" :size="20" />
            <span>{{
              t("dashboard:fieldObservations.partsTree.saveTreeAsTemplate")
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Add Part Modal -->
    <Teleport to="body">
      <div
        v-if="showAddModal"
        class="popup-overlay"
        @click.self="showAddModal = false"
      >
        <div class="popup-content">
          <h4>
            {{
              addPartParentId
                ? t("dashboard:fieldObservations.part.createSubPart")
                : t("dashboard:fieldObservations.part.create")
            }}
          </h4>
          <input
            v-model="newPartName"
            type="text"
            class="popup-input"
            :placeholder="t('dashboard:fieldObservations.part.placeholder')"
            @keyup.enter="handleAddPart"
          />
          <div class="popup-actions">
            <button class="popup-btn cancel" @click="showAddModal = false">
              {{ t("cancel") }}
            </button>
            <button
              class="popup-btn confirm"
              :disabled="!newPartName.trim() || isCreating"
              @click="handleAddPart"
            >
              <span
                v-if="isCreating"
                class="spinner-border spinner-border-sm me-2"
              />
              {{ t("add") }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Edit Part Modal -->
    <Teleport to="body">
      <div v-if="showEditModal" class="popup-overlay" @click.self="cancelEdit">
        <div class="popup-content">
          <h4>{{ t("dashboard:fieldObservations.part.edit") }}</h4>
          <input
            v-model="editingName"
            type="text"
            class="popup-input"
            :placeholder="t('dashboard:fieldObservations.part.placeholder')"
            @keyup.enter="saveEdit"
          />
          <div class="popup-actions">
            <button class="popup-btn cancel" @click="cancelEdit">
              {{ t("cancel") }}
            </button>
            <button
              class="popup-btn confirm"
              :disabled="!editingName.trim() || isSavingEdit"
              @click="saveEdit"
            >
              <span
                v-if="isSavingEdit"
                class="spinner-border spinner-border-sm me-2"
              />
              {{ t("save") }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <MobileConfirmModal
      v-model="showDeleteConfirm"
      :title="t('dashboard:fieldObservations.partsTree.confirmDelete.title')"
      :message="
        t('dashboard:fieldObservations.partsTree.confirmDelete.message')
      "
      :confirm-label="t('delete')"
      :loading="isDeleting"
      danger
      @confirm="handleDelete"
    />

    <!-- Cannot Delete Modal -->
    <MobileConfirmModal
      v-model="showCannotDelete"
      :title="t('dashboard:fieldObservations.partsTree.cannotDelete')"
      :message="cannotDeleteReason"
      :confirm-label="t('ok')"
      hide-cancel
      @confirm="showCannotDelete = false"
    />

    <!-- Save As Template Modal -->
    <SaveAsTemplateModal
      v-model="showSaveTemplateModal"
      :part="partToSaveAsTemplate"
      :all-parts="parts ?? []"
    />

    <!-- Insert From Template Modal -->
    <InsertFromTemplateModal
      v-model="showInsertTemplateModal"
      :diagnostic-id="props.diagnosticId"
      :parent-part-id="insertTemplateParentId"
      @inserted="handleTemplateInserted"
    />

    <!-- Multiply Modal -->
    <Teleport to="body">
      <div
        v-if="showMultiplyModal"
        class="popup-overlay"
        @click.self="showMultiplyModal = false"
      >
        <div class="popup-content">
          <h4>
            {{
              t("dashboard:fieldObservations.partsTree.multiplyTitle", {
                name: multiplyPart?.name,
              })
            }}
          </h4>

          <div class="multiply-list">
            <div
              v-for="(name, idx) in multiplyNames"
              :key="idx"
              class="multiply-row"
            >
              <span class="multiply-index">{{ idx + 1 }}</span>
              <input
                v-model="multiplyNames[idx]"
                type="text"
                class="popup-input"
                :placeholder="t('dashboard:fieldObservations.part.placeholder')"
              />
            </div>
          </div>

          <div class="multiply-actions-row">
            <button class="multiply-btn" @click="addMultiplyCopy">
              <PhosphorIcon name="plus" :size="16" />
              {{ t("dashboard:fieldObservations.partsTree.multiplyAdd") }}
            </button>
            <button
              v-if="multiplyNames.length > 2"
              class="multiply-btn multiply-btn-remove"
              @click="removeMultiplyCopy"
            >
              <PhosphorIcon name="minus" :size="16" />
              {{ t("dashboard:fieldObservations.partsTree.multiplyRemove") }}
            </button>
          </div>

          <div class="popup-actions">
            <button class="popup-btn cancel" @click="showMultiplyModal = false">
              {{ t("cancel") }}
            </button>
            <button
              class="popup-btn confirm"
              :disabled="
                multiplyNames.filter((n) => n.trim()).length < 2 ||
                isDuplicating
              "
              @click="handleMultiply"
            >
              <span
                v-if="isDuplicating"
                class="spinner-border spinner-border-sm me-2"
              />
              {{ t("confirm") }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.parts-tree-editor {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: var(--bs-white);
  display: flex;
  flex-direction: column;
}

.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 8px;
}

.editor-content-inner {
  max-width: 600px;
  margin: 0 auto;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--bs-secondary-color);
  text-align: center;

  .empty-icon {
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.parts-tree {
  display: flex;
  flex-direction: column;
}

.part-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: transparent;
}

.level-indicator {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: var(--bs-border-color);
}

.btn-expand {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: var(--bs-secondary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background-color: var(--bs-secondary-bg);
  }
}

.expand-icon {
  transition: transform 0.2s ease;

  &.expanded {
    transform: rotate(90deg);
  }
}

.expand-spacer {
  width: 24px;
  flex-shrink: 0;
}

.part-name {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--bs-body-color);
}

.observation-badge {
  padding: 2px 8px;
  border-radius: 12px;
  background-color: var(--bs-dark);
  color: white;
  font-size: 12px;
  font-weight: 600;
}

.part-actions {
  display: flex;
  gap: 6px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50px;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &.btn-primary {
    background-color: white;
    border: 1px solid var(--bs-primary);
    color: var(--bs-primary);
  }

  &.btn-delete {
    background-color: #ef4444;
    color: white;
  }
}

.edit-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--bs-primary);
  border-radius: 8px;
  background-color: var(--bs-white);
  font-size: 15px;
  color: var(--bs-body-color);

  &:focus {
    outline: none;
  }
}

.root-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.btn-add-root,
.btn-insert-template {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  border: 2px dashed var(--bs-border-color);
  border-radius: calc(var(--bs-border-radius) * 2);
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }
}

.multiply-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.multiply-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.multiply-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  color: white;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.multiply-actions-row {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.multiply-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px dashed var(--bs-border-color);
  border-radius: 10px;
  background-color: transparent;
  color: var(--bs-secondary-color);
  font-size: 14px;
  cursor: pointer;

  &:hover {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }

  &.multiply-btn-remove:hover {
    border-color: var(--bs-danger);
    color: var(--bs-danger);
  }
}

// Popup styles
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}

.popup-content {
  width: 100%;
  background-color: var(--bs-white);
  border-radius: 20px 20px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));

  h4 {
    margin: 0 0 16px;
    font-size: 18px;
    font-weight: 600;
  }
}

.popup-input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background-color: var(--bs-tertiary-bg);
  font-size: 16px;
  color: var(--bs-body-color);

  &:focus {
    outline: none;
    border-color: var(--bs-primary);
  }
}

.popup-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.popup-btn {
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &.cancel {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);
  }

  &.confirm {
    background-color: var(--bs-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.danger {
    background-color: #ef4444;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
